import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

import { chatService } from '../../services/chatService';
import { format } from 'date-fns';

const ChatAnalyticsDashboard = () => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState([]);
  const [commonQuestions, setCommonQuestions] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    status: ''
  });
  const [stats, setStats] = useState({
    totalSessions: 0,
    activeSessions: 0,
    completedSessions: 0,
    abandonedSessions: 0,
    avgDuration: 0,
    avgMessagesPerSession: 0,
    escalationRate: 0
  });

  useEffect(() => {
    if (user) {
      fetchAnalytics();
      fetchCommonQuestions();
      fetchEscalations();
    }
  }, [user, filters]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const result = await chatService?.getChatAnalytics(filters);
      if (result?.success) {
        setAnalytics(result?.data || []);
        calculateStats(result?.data || []);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommonQuestions = async () => {
    try {
      const result = await chatService?.getCommonQuestions(10);
      if (result?.success) {
        setCommonQuestions(result?.data || []);
      }
    } catch (error) {
      console.error('Error fetching common questions:', error);
    }
  };

  const fetchEscalations = async () => {
    try {
      const result = await chatService?.getEscalationMetrics(filters);
      if (result?.success) {
        setEscalations(result?.data || []);
      }
    } catch (error) {
      console.error('Error fetching escalations:', error);
    }
  };

  const calculateStats = (data) => {
    const total = data?.length || 0;
    const active = data?.filter(s => s?.status === 'active')?.length || 0;
    const completed = data?.filter(s => s?.status === 'completed')?.length || 0;
    const abandoned = data?.filter(s => s?.status === 'abandoned')?.length || 0;
    
    const totalDuration = data?.reduce((sum, s) => sum + (s?.duration_seconds || 0), 0);
    const avgDuration = total > 0 ? Math.round(totalDuration / total) : 0;
    
    const totalMessages = data?.reduce((sum, s) => sum + (s?.message_count || 0), 0);
    const avgMessages = total > 0 ? Math.round(totalMessages / total) : 0;
    
    const escalatedCount = data?.filter(s => s?.chat_analytics?.[0]?.escalated)?.length || 0;
    const escalationRate = total > 0 ? Math.round((escalatedCount / total) * 100) : 0;

    setStats({
      totalSessions: total,
      activeSessions: active,
      completedSessions: completed,
      abandonedSessions: abandoned,
      avgDuration,
      avgMessagesPerSession: avgMessages,
      escalationRate
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'abandoned': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const StatCard = ({ title, value, icon, trend, color = 'blue' }) => (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-3xl font-bold text-${color}-600 mt-2`}>{value}</p>
          {trend && (
            <p className="text-xs text-gray-500 mt-1">{trend}</p>
          )}
        </div>
        <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
          <Icon name={icon} size={24} className={`text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1FB5A5]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Chat Analytics Dashboard</h1>
          <p className="text-gray-600 mt-2">Monitor AI chat conversations, user engagement, and conversion metrics</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Sessions"
            value={stats?.totalSessions}
            icon="MessageSquare"
            color="blue"
            trend={null}
          />
          <StatCard
            title="Active Sessions"
            value={stats?.activeSessions}
            icon="Activity"
            color="green"
            trend={null}
          />
          <StatCard
            title="Avg Duration"
            value={formatDuration(stats?.avgDuration)}
            icon="Clock"
            color="purple"
            trend={null}
          />
          <StatCard
            title="Escalation Rate"
            value={`${stats?.escalationRate}%`}
            icon="AlertTriangle"
            color="red"
            trend={null}
          />
        </div>

        {/* Common Questions */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Common Questions</h2>
            <Icon name="TrendingUp" size={20} className="text-gray-400" />
          </div>
          <div className="space-y-4">
            {commonQuestions?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No common questions tracked yet</p>
            ) : (
              commonQuestions?.map((question, index) => (
                <div key={question?.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4 flex-1">
                    <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{question?.question_text}</p>
                      <p className="text-sm text-gray-500">{question?.question_category}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#1FB5A5]">{question?.frequency_count}</p>
                      <p className="text-xs text-gray-500">times asked</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Sessions */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Recent Chat Sessions</h2>
            <div className="flex items-center space-x-2">
              <select
                value={filters?.status}
                onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1FB5A5]"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="abandoned">Abandoned</option>
              </select>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">User</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Page Context</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Messages</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Duration</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Started</th>
                </tr>
              </thead>
              <tbody>
                {analytics?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-500">
                      No chat sessions found
                    </td>
                  </tr>
                ) : (
                  analytics?.map((session) => (
                    <tr key={session?.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-[#1FB5A5] rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-medium">
                              {session?.user_name?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900">{session?.user_name || 'Anonymous'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600 capitalize">{session?.page_context || 'N/A'}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session?.status)}`}>
                          {session?.status}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-900">{session?.message_count || 0}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600">{formatDuration(session?.duration_seconds)}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600">
                          {session?.started_at ? format(new Date(session?.started_at), 'MMM dd, HH:mm') : 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Escalations */}
        {escalations?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Escalated Conversations</h2>
              <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                {escalations?.length} escalations
              </span>
            </div>
            <div className="space-y-4">
              {escalations?.map((escalation) => (
                <div key={escalation?.id} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Reason: {escalation?.escalation_reason?.replace('_', ' ')}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Session ID: {escalation?.session_id?.substring(0, 8)}...
                      </p>
                    </div>
                    <Icon name="AlertTriangle" size={20} className="text-red-600" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ChatAnalyticsDashboard;